/**
 * EventWindows Component
 *
 * This component is copied from the Opinion module:
 * @see src/pages/opinion/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/BarAndLine/EventWindows/index.tsx
 *
 * The core logic (positioning, collision detection, batch rendering) remains identical.
 * The only difference is the EventItem component, which has custom styling and content for the Strategic module.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import EventItem from './EventItem';
import { ParamsTimeType } from '@op/types/common';
import { sortTimeArrayListFunc } from '@op/utils';
interface DivData {
    eventLine: HTMLDivElement;
    eventBox: HTMLDivElement;
    width: number;
    height: number;
    left: number;
    show?: boolean;
    top?: number;
    bottom: number;
    maxHeight: number;
    direction: 'left' | 'right';
    isLast?: boolean;
}

export default function EventWindows({
    myChart,
    eventList,
    format,
    maxTimeValue,
    showEventAxis,
    dateType,
    xAxisData,
    yAxisMin,
    topPadding = 0,
    eventBoxClassName = '',
    // onMaxHeightChange,
}: {
    myChart: any;
    eventList: any;
    format: string;
    maxTimeValue: string;
    showEventAxis: boolean;
    dateType: ParamsTimeType;
    xAxisData: Array<string>;
    yAxisMin?: boolean;
    topPadding?: number;
    eventBoxClassName?: string;
    // onMaxHeightChange?: (maxHeight: number) => void;
}) {
    const FIXED_CHART_HEIGHT = 443;
    const [renderHide, setRenderHide] = useState(true);
    const halfBarWidthRef = useRef<number>(0);
    const renderPositionMap = useRef<any>({});
    function getBarInfo({ seriesName }: { seriesName: string }) {
        try {
            // 获取柱子的布局矩形
            const seriesModel = myChart.getModel().getSeriesByName(seriesName)[0];
            const layoutRect = seriesModel.getData().getItemLayout(0);
            // 计算柱子的宽度
            const barWidth = layoutRect?.width || 0;

            return {
                barWidth,
                halfBarWidth: barWidth / 2,
            };
        } catch (error) {
            return { barWidth: 0, halfBarWidth: 0 };
        }
    }

    /**
     * 放置板块逻辑
     *
     * 放置板块开始循环 top = 0 到 top = 最大高度[280] - 板块高度 ，寻找位置，尝试放置
     * 循环已放置的板块,并判断正在放置的板块和已放置板块是否重叠
     *
     * 发现与所有已放置的板块都重叠，设置show为false
     * 如果找到了一个不与已放置板块重叠的位置，则设置show为true
     * @param divs
     * @returns
     */
    function placeDivs(divs: DivData[]) {
        // 这里控制底部留下多少空白 ， 加油一个气泡高度dbo-event-box (100 is the white space)
        const maxHeight = FIXED_CHART_HEIGHT - 100;

        function countCurrentPosition(currentDiv: DivData, currentIndex: number) {
            currentDiv.top = topPadding; // 初始化 top 值
            let isPlaced = false;

            while (currentDiv.top <= maxHeight - currentDiv.height && !isPlaced) {
                isPlaced = true;
                for (let i = 0; i < currentIndex; i++) {
                    const placedDiv = divs[i];
                    if (placedDiv.show !== false) {
                        // 检查是否重叠
                        if (
                            currentDiv.left < placedDiv.left + placedDiv.width &&
                            currentDiv.left + currentDiv.width > placedDiv.left &&
                            (currentDiv.top ?? 0) < (placedDiv.top ?? 0) + placedDiv.height &&
                            (currentDiv.top ?? 0) + currentDiv.height > (placedDiv.top ?? 0)
                        ) {
                            isPlaced = false;
                            break;
                        }
                    }
                }

                if (!isPlaced) {
                    currentDiv.top += 1; // 尝试下一个位置
                } else {
                    currentDiv.show = true;
                }
            }

            if (!isPlaced) {
                currentDiv.show = false;
            }
        }

        // 最后一个板块尝试减少高度进行强制放置
        divs.forEach((currentDiv, currentIndex) => {
            countCurrentPosition(currentDiv, currentIndex);

            if (currentIndex === divs.length - 1 && currentDiv.show === false) {
                while (currentDiv.height > 40) {
                    currentDiv.height = currentDiv.height - 1;
                    countCurrentPosition(currentDiv, currentIndex);
                    // console.log(`Reducing height to ${currentDiv.height}`); // 添加日志


                    if (currentDiv.show) {
                        // 因为设置的是内容高度，所以减18   外部盒子和内部盒子相差18px
                        // currentDiv.height = currentDiv.height - 18;
                        currentDiv.height = currentDiv.height;
                        break;
                    }
                }

                currentDiv.isLast = true;
            }
        });

        return divs;
    }

    /**
     * 批量设置事件气泡的布局和样式
     * 此函数根据提供的数据处理一组事件气泡（div元素），包括定位、显示和隐藏等操作
     * @param processedDivs 一个包含事件气泡相关数据的数组，用于批量设置
     */
    function batchSettingEventBubble(processedDivs: DivData[]) {
        let maxBottomPosition = 0;

        // 遍历每个事件气泡数据项
        processedDivs.forEach(
            ({
                eventBox,
                eventLine,
                left,
                top,
                bottom,
                height,
                show,
                width,
                direction,
                maxHeight,
                isLast,
            }) => {
                // 如果事件气泡应该显示
                if (show) {
                    // 设置事件气泡的位置
                    eventBox.style.left = left + 'px';
                    eventBox.style.top = top + 'px';
                    if (isLast) {
                        const eventBoxChildren: any =
                            eventBox.querySelector('.dbs-event-list'); // use dbs-event-list instead of dbs-event-box because our EventItem wrap in deeper layer
                        if (eventBoxChildren) {
                            eventBoxChildren.style.height = height + 'px';
                        }
                    }

                    // 尝试获取事件气泡中的线条元素
                    // 如果线条元素存在，设置其样式
                    // 计算线条的top和left位置，以及高度
                    const lineTop = (top ?? 0) + height - 8;
                    const lineLeft = direction === 'left' ? left : left + width - 2;
                    const lineHeight = maxHeight - (top || 0) - height - bottom + 24; // the 24 is adjustable according to the situation

                    // 设置线条的样式
                    eventLine.style.top = lineTop + 'px';
                    eventLine.style.left = lineLeft + 'px';
                    eventLine.style.height = lineHeight + 'px';

                    // Calculate the bottom position of event bubble (without line)
                    // console.log("top: ", top, "height: ", height)
                    const eventBottom = (top ?? 0) + height;
                    maxBottomPosition = Math.max(maxBottomPosition, eventBottom);
                } else {
                    // 如果事件气泡不应该显示，则将其隐藏
                    eventBox.style.display = 'none';
                    eventLine.style.display = 'none';
                }
            },
        );

        // Report the max height back to parent component
        // if (onMaxHeightChange && maxBottomPosition > 0) {
        //     const maxHeightWithPadding = maxBottomPosition;
        //     // console.log('[EventWindows] Max bubble height:', maxHeightWithPadding);
        //     onMaxHeightChange(maxHeightWithPadding);
        // }
    }

    /**
     * 获取事件板块的位置信息
     * 这里处理了边缘事件展示的逻辑，调整left值，并添加标识direction
     * @param eventBox
     * @param left
     * @param maxWidth
     * @param barWidth
     * @returns
     */
    function getEventItemInfo({
        eventBox,
        left,
        maxWidth,
        barWidth,
        barBottomPosition,
        maxHeight,
        eventLine,
    }: {
        eventBox: HTMLDivElement;
        left: number;
        maxWidth: number;
        barWidth: number;
        barBottomPosition: number;
        maxHeight: number;
        eventLine: HTMLDivElement;
    }): DivData | null {
        const rect = eventBox.getBoundingClientRect();
        const { width, height } = rect;

        // Safari fix: If getBoundingClientRect returns 0, skip this item
        // This can happen when Safari hasn't completed layout yet
        if (width === 0 || height === 0) {
            return null;
        }

        let realLeft = left;
        let direction: 'left' | 'right' = 'left';
        if (width + left > maxWidth) {
            realLeft = left + barWidth - width;
            direction = 'right';
        }

        return {
            width,
            height,
            bottom: maxHeight - barBottomPosition,
            maxHeight,
            left: realLeft,
            eventBox,
            eventLine,
            direction,
        };
    }

    useEffect(() => {
        // Use requestAnimationFrame to ensure Safari has completed layout before measuring
        // This fixes getBoundingClientRect returning 0 in Safari
        const rafId = requestAnimationFrame(() => {
            if (!myChart) {
                return;
            }
            if (typeof myChart.isDisposed === 'function' && myChart.isDisposed()) {
                return;
            }
            const { halfBarWidth, barWidth } = getBarInfo({
                seriesName: 'TopTipsEvent',
            });
            halfBarWidthRef.current = halfBarWidth;
            const chartWidth = myChart?.getWidth?.() || 0;
            if (chartWidth <= 0) {
                return;
            }
            const keys = Object.keys(renderPositionMap.current);

            const renderList = keys
                .map((key) => {
                    const { barLeftPosition, eventBox, barBottomPosition, eventLine } =
                        renderPositionMap.current[key] || {};
                    if (!barLeftPosition || !eventBox) return null;
                    const left = barLeftPosition - halfBarWidth;
                    return getEventItemInfo({
                        eventBox,
                        left,
                        maxWidth: chartWidth,
                        maxHeight: FIXED_CHART_HEIGHT,
                        barWidth,
                        barBottomPosition,
                        eventLine,
                    });
                })
                .filter((item): item is DivData => item !== null && item !== undefined);

            const processedDivs = placeDivs(renderList);

            batchSettingEventBubble(processedDivs);
        });

        return () => cancelAnimationFrame(rafId);
    }, [renderHide, eventList]);

    const eventListRender = useMemo(() => {
        const list = Object.keys(eventList || {});
        list.sort(sortTimeArrayListFunc());
        return list.map((xAxisValue) => {
            const list = eventList[xAxisValue];
            return {
                key: xAxisValue,
                children: list,
            };
        });
    }, [eventList]);

    return (
        <>
            {eventListRender.map(({ key, children }, index) => {
                return (
                    <EventItem
                        myChart={myChart}
                        xAxisValue={key}
                        eventList={children}
                        key={key}
                        hide={renderHide}
                        format={format}
                        dateType={dateType}
                        maxTimeValue={maxTimeValue}
                        showEventAxis={showEventAxis}
                        halfBarWidth={halfBarWidthRef.current}
                        xAxisData={xAxisData}
                        yAxisMin={yAxisMin}
                        eventBoxClassName={eventBoxClassName}
                        backPositionInfo={(item) => {
                            // setRenderPositionMap({ ...renderPositionMap, [index]: item });
                            renderPositionMap.current = {
                                ...renderPositionMap.current,
                                [index]: item,
                            };

                            if (
                                Object.keys(renderPositionMap.current).length ===
                                Object.keys(eventList || {}).length
                            ) {
                                setRenderHide(false);
                            }
                        }}
                    ></EventItem>
                );
            })}
        </>
    );
}
